import { useToast } from "../../../../components/common/toasts/ToastContext";
import ErrorToast from "../../../../components/common/toasts/ErrorToast";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { premiumRequest } from "../../../../services/Premium";
import { premiumPaths } from "../../navigation";
import useAsyncAction from "../../hooks/useAsyncAction";
import ProgressBar from "../../../../components/common/ProgressBar";

/** Przygotowuje komplet dokumentów i zapisuje zlecenie, zachowując klucz ponowienia. */
export default function OrderForm({ organization, token, onCreated }) {
  const { notify } = useToast();
  const requirements = organization.plugin?.requirements;
  const limits = requirements?.limits;
  const accept = [
    ...new Set(
      requirements?.documents.flatMap((item) => item.extensions) || [],
    ),
  ]
    .map((extension) => `.${extension}`)
    .join(",");
  const documentOptions = (name) =>
    requirements.documents.filter((item) =>
      item.extensions.includes(name.split(".").pop().toLowerCase()),
    );
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [uploadProgress, setUploadProgress] = useState(0);
  const { busy, error, setError, run } = useAsyncAction(
    `${token}:${organization.id}`,
  );
  const changeFiles = (next) => {
    setFiles(next);
    setRequestKey(crypto.randomUUID());
  };
  const addFiles = (fileList) => {
    if (busy) return;
    const added = Array.from(fileList || []);
    if (!added.length) return;
    const unsupported = added.find(
      (file) => !documentOptions(file.name).length,
    );
    if (unsupported) {
      notify(
        `Nieobsługiwany format pliku: ${unsupported.name}. Dozwolone rozszerzenia: ${accept}.`,
        "warning",
      );
      return;
    }
    const next = [
      ...files,
      ...added.map((file) => ({
        file,
        kind: documentOptions(file.name)[0]?.kind || "",
        id: crypto.randomUUID(),
      })),
    ];
    if (
      next.length > limits.files ||
      next.reduce((sum, entry) => sum + entry.file.size, 0) >
        limits.totalBytes ||
      added.some((file) => file.size > limits.fileBytes)
    ) {
      notify(
        `Dodaj maksymalnie ${limits.files} plików, do ${limits.fileBytes / 1024 / 1024} MB każdy i ${limits.totalBytes / 1024 / 1024} MB łącznie.`,
        "warning",
      );
    } else {
      changeFiles(next);
      setError("");
    }
  };
  const dragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !Array.from(event.dataTransfer.types).includes("Files")) return;
    dragDepth.current += 1;
    setDragging(true);
  };
  const dragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect =
      !busy && Array.from(event.dataTransfer.types).includes("Files")
        ? "copy"
        : "none";
  };
  const dragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const dropFiles = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    addFiles(event.dataTransfer.files);
  };
  const submit = (event) => {
    event.preventDefault();
    if (!complete || !organization.active) return;
    run(async (isActive) => {
      setUploadProgress(0);
      const data = new FormData();
      data.append("requestKey", requestKey);
      files.forEach(({ file, kind }) => {
        data.append("files", file);
        data.append("kinds", kind);
      });
      const result = await premiumRequest(
        token,
        `/organizations/${organization.id}/orders`,
        {
          method: "POST",
          data,
          onUploadProgress: (event) => {
            if (isActive())
              setUploadProgress(
                event.total > 0 ? (event.loaded / event.total) * 100 : null,
              );
          },
        },
      );
      if (isActive()) onCreated(result.order);
    });
  };
  if (!requirements)
    return (
      <ErrorToast message="Nie można wczytać konfiguracji dokumentów firmy. Odśwież stronę." />
    );
  const complete =
    files.length > 0 &&
    files.every((entry) =>
      documentOptions(entry.file.name).some(
        (option) => option.kind === entry.kind,
      ),
    ) &&
    requirements.alternatives.some((group) =>
      group.every((kind) => files.some((entry) => entry.kind === kind)),
    );
  return (
    <form className="card premium-panel" onSubmit={submit}>
      <h2>Dodaj dokumenty jednej dostawy</h2>
      <p className="text-muted">
        Dokumenty utworzą projekt jednego świadectwa. Możesz sprawdzić dane i
        podgląd PDF bez wystawiania. Wystawienie wymaga osobnego potwierdzenia.{" "}
        {requirements.hint}
      </p>
      <ErrorToast message={error} />
      {busy && (
        <ProgressBar
          label={
            uploadProgress >= 100
              ? "Zapisywanie zlecenia"
              : "Wysyłanie dokumentów"
          }
          value={uploadProgress >= 100 ? undefined : uploadProgress}
          description={
            uploadProgress >= 100
              ? "Pliki zostały wysłane. Czekamy na zapis zlecenia."
              : "Po zapisaniu dokumentów otworzymy ekran świadectwa."
          }
        />
      )}
      <fieldset disabled={busy}>
        <label
          className={`premium-dropzone${dragging && !busy ? " premium-dropzone-dragging" : ""}`}
          aria-disabled={busy}
          onDragEnter={dragEnter}
          onDragOver={dragOver}
          onDragLeave={dragLeave}
          onDrop={dropFiles}
        >
          {requirements.uploadLabel}
          <input
            type="file"
            multiple
            accept={accept}
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = "";
            }}
            className="form-control premium-file-input"
          />
          <small>Przeciągnij pliki tutaj lub wybierz je z dysku.</small>
          <small>
            Do {limits.files} plików · {limits.fileBytes / 1024 / 1024} MB na
            plik
          </small>
        </label>
        <ul className="premium-files">
          {files.map((entry, index) => (
            <li key={entry.id}>
              <span>
                {entry.file.name}{" "}
                <small>({Math.ceil(entry.file.size / 1024)} KB)</small>
              </span>
              <select
                aria-label={`Rodzaj pliku ${entry.file.name}`}
                className="form-select"
                value={entry.kind}
                onChange={(event) =>
                  changeFiles(
                    files.map((item, i) =>
                      i === index
                        ? { ...item, kind: event.target.value }
                        : item,
                    ),
                  )
                }
              >
                {documentOptions(entry.file.name).map((option) => (
                  <option key={option.kind} value={option.kind}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline-danger"
                aria-label={`Usuń ${entry.file.name}`}
                onClick={() => changeFiles(files.filter((_, i) => i !== index))}
              >
                Usuń
              </button>
            </li>
          ))}
        </ul>
        <div className="premium-form-actions">
          <Link
            className="btn btn-outline-primary"
            to={premiumPaths.orders(organization.id)}
          >
            Anuluj
          </Link>
          <button
            className="btn btn-primary"
            disabled={!complete || !organization.active}
          >
            {busy ? "Przesyłanie…" : "Odczytaj dokumenty"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}
